import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bot, Video, CreditCard, MessageSquare, Save, KeyRound } from "lucide-react";

const SUB = [
  { id: "ai", label: "AI & LLM", icon: Bot },
  { id: "auth", label: "API & Auth Integrations", icon: KeyRound },
  { id: "cdn", label: "Video & CDN", icon: Video },
  { id: "pay", label: "Payment Gateways", icon: CreditCard },
  { id: "comms", label: "Comms & Meetings", icon: MessageSquare },
];

function Msg91HealthCard() {
  const [cfg, setCfg] = useState(null);
  const [ping, setPing] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/platform/diagnostics/msg91-config").then(r => setCfg(r.data)).catch(() => {}); }, []);
  const doPing = async () => {
    setBusy(true); setPing(null);
    try { const r = await api.get("/platform/diagnostics/msg91-ping"); setPing(r.data); }
    catch (e) { setPing({ status: "error", message: e?.response?.data?.detail || "Ping failed" }); }
    setBusy(false);
  };
  const badge = cfg?.status_badge;
  const badgeUI = { connected: ["bg-[#86EFAC]", "Connected"], partial: ["bg-[#FDE047]", "Partial (SMS Only)"], not_configured: ["bg-[#FCA5A5]", "Not Configured"] }[badge] || ["bg-slate-200", "…"];
  return (
    <div className="nb-card p-4 bg-[#F5F3FF] border-2 border-black mt-3 space-y-2" data-testid="msg91-health-card">
      <div className="flex items-center justify-between">
        <h4 className="font-bold text-sm">MSG91 Configuration & Health (WhatsApp / WABA)</h4>
        <span className={`nb-badge ${badgeUI[0]}`} data-testid="msg91-status-badge">{badgeUI[1]}</span>
      </div>
      {cfg && (
        <div className="grid md:grid-cols-2 gap-2 text-xs">
          <div>Auth Key: <span className="mono">{cfg.auth_key_masked || "Not configured"}</span></div>
          <div>Integrated Number: <span className="mono">{cfg.integrated_number_masked || "Not configured"}</span></div>
          <div>Namespace: <span className="mono">{cfg.namespace_present ? "set" : "not set (optional)"}</span></div>
          <div className="mono break-all opacity-70">{cfg.endpoint}</div>
        </div>
      )}
      <button className="nb-btn text-xs" onClick={doPing} disabled={busy} data-testid="msg91-ping-btn">
        {busy ? "Pinging…" : "Ping MSG91 API"}
      </button>
      {ping && (
        <div className={`nb-card-sm p-2 text-xs border-2 border-black ${ping.status === "success" ? "bg-[#DCFCE7]" : "bg-[#FEE2E2]"}`} data-testid="msg91-ping-result">
          <b>{ping.status === "success" ? "✓ Success" : "✕ Error"}</b>{ping.http_code ? ` (HTTP ${ping.http_code})` : ""} — {ping.message}
        </div>
      )}
    </div>
  );
}

function Field({ s, set, k, label, type = "text", secret }) {
  const isSet = s[`${k}__set`];
  return (
    <label className="block">
      <span className="nb-label text-[11px]">{label}{secret && isSet && <span className="ml-1 text-green-700">• saved</span>}</span>
      <input type={type} className="nb-input mono text-sm mt-1" value={s[k] ?? ""}
        placeholder={secret ? "•••• (enter to replace)" : ""}
        onChange={e => set(k, e.target.value)} data-testid={`ps-${k}`} />
    </label>
  );
}

export default function PlatformSettings() {
  const [sub, setSub] = useState("ai");
  const [s, setS] = useState(null);
  const [dirty, setDirty] = useState({});
  const load = () => api.get("/global-admin/platform-settings").then(r => { setS(r.data); setDirty({}); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => { setS({ ...s, [k]: v }); setDirty({ ...dirty, [k]: v }); };
  const save = async () => {
    try { const r = await api.patch("/global-admin/platform-settings", dirty); setS(r.data); setDirty({}); toast.success("Platform settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!s) return <div className="mono">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="platform-settings">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="flex gap-2 flex-wrap">
          {SUB.map(t => { const I = t.icon; return (
            <button key={t.id} onClick={() => setSub(t.id)} data-testid={`ps-tab-${t.id}`}
              className={`nb-btn text-xs ${sub === t.id ? "" : "nb-btn-ghost"}`}><I size={14} /> {t.label}</button>
          ); })}
        </div>
        <button className="nb-btn" onClick={save} disabled={!Object.keys(dirty).length} data-testid="ps-save"><Save size={14} /> Save</button>
      </div>

      <div className="nb-card p-6 max-w-2xl space-y-3">
        {sub === "ai" && <>
          <h3 className="display text-lg font-bold">AI & LLM Engine</h3>
          <Field s={s} set={set} k="platform_gemini_api_key" label="System Gemini API Key (background moderation)" type="password" secret />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!s.enforce_creator_byok} onChange={e => set("enforce_creator_byok", e.target.checked)} data-testid="ps-enforce-byok" />
            Enforce Creator BYOK for LMS Tools
          </label>
          <p className="text-xs opacity-60">Setting a system key enables live AI screening (replaces the manual-review fallback).</p>
        </>}

        {sub === "auth" && <>
          <h3 className="display text-lg font-bold">API &amp; Auth Integrations</h3>
          <p className="text-xs opacity-70">OAuth credentials for social sign-in used by the Open School wizard and dashboards. Email + password sign-up works without these.</p>
          <h4 className="font-bold text-sm pt-1">Google OAuth (Sign-in)</h4>
          <div className="grid md:grid-cols-2 gap-3">
            <Field s={s} set={set} k="google_oauth_client_id" label="Google Client ID" />
            <Field s={s} set={set} k="google_oauth_client_secret" label="Google Client Secret" type="password" secret />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!s.google_oauth_enabled} onChange={e => set("google_oauth_enabled", e.target.checked)} data-testid="ps-google-enabled" />
            Enable &quot;Continue with Google&quot; on sign-in &amp; onboarding
          </label>
        </>}

        {sub === "cdn" && <>
          <h3 className="display text-lg font-bold">Video Infrastructure &amp; CDN</h3>
          <Field s={s} set={set} k="youtube_api_key" label="YouTube Data API v3 Key" type="password" secret />
          <div className="grid md:grid-cols-2 gap-3">
            <Field s={s} set={set} k="bunny_storage_zone" label="Bunny Storage Zone Name" />
            <Field s={s} set={set} k="bunny_pull_zone_url" label="Bunny Pull Zone URL" />
          </div>
          <Field s={s} set={set} k="bunny_storage_access_key" label="Bunny Storage Access Key" type="password" secret />
        </>}

        {sub === "pay" && <>
          <h3 className="display text-lg font-bold">Payment Gateways</h3>
          <div className="nb-card-sm p-3 bg-[#FDE047] border-2 border-black">
            <span className="nb-label text-[11px]">Environment Mode</span>
            <div className="flex gap-3 mt-1">
              {["test", "live"].map(m => (
                <label key={m} className="flex items-center gap-1 text-sm font-bold">
                  <input type="radio" name="gwmode" checked={s.payment_gateway_mode === m} onChange={() => set("payment_gateway_mode", m)} data-testid={`ps-mode-${m}`} />
                  {m.toUpperCase()}
                </label>
              ))}
            </div>
            <p className="text-[10px] opacity-70 mt-1">Entering credentials never auto-switches to LIVE.</p>
          </div>
          <h4 className="font-bold text-sm pt-1">Razorpay (India / INR)</h4>
          <div className="grid md:grid-cols-2 gap-3">
            <Field s={s} set={set} k="razorpay_key_id" label="Key ID" secret />
            <Field s={s} set={set} k="razorpay_key_secret" label="Key Secret" type="password" secret />
            <Field s={s} set={set} k="razorpay_webhook_secret" label="Webhook Secret" type="password" secret />
            <Field s={s} set={set} k="razorpay_route_account_id" label="Route Account ID" />
          </div>
          <h4 className="font-bold text-sm pt-1">Stripe (US / Global)</h4>
          <div className="grid md:grid-cols-2 gap-3">
            <Field s={s} set={set} k="stripe_publishable_key" label="Publishable Key" secret />
            <Field s={s} set={set} k="stripe_secret_key" label="Secret Key" type="password" secret />
            <Field s={s} set={set} k="stripe_webhook_secret" label="Webhook Signing Secret" type="password" secret />
            <Field s={s} set={set} k="stripe_connect_platform_id" label="Connect Platform ID" />
          </div>
        </>}

        {sub === "comms" && <>
          <h3 className="display text-lg font-bold">Communications & Live Meetings</h3>
          <h4 className="font-bold text-sm">Microsoft Teams</h4>
          <div className="grid md:grid-cols-3 gap-3">
            <Field s={s} set={set} k="ms_teams_client_id" label="Client ID" />
            <Field s={s} set={set} k="ms_teams_client_secret" label="Client Secret" type="password" secret />
            <Field s={s} set={set} k="ms_teams_tenant_id" label="Tenant ID" />
          </div>
          <h4 className="font-bold text-sm pt-1">Google Meet / Workspace</h4>
          <div className="grid md:grid-cols-2 gap-3">
            <Field s={s} set={set} k="google_meet_client_id" label="OAuth Client ID" />
            <Field s={s} set={set} k="google_meet_client_secret" label="Client Secret" type="password" secret />
          </div>
          <h4 className="font-bold text-sm pt-1">SMS Notifications (Twilio / Fast2SMS)</h4>
          <div className="grid md:grid-cols-3 gap-3">
            <Field s={s} set={set} k="twilio_account_sid" label="Account SID" type="password" secret />
            <Field s={s} set={set} k="twilio_auth_token" label="Auth Token" type="password" secret />
            <Field s={s} set={set} k="twilio_sender_phone" label="Sender Phone" />
          </div>
          <p className="text-xs opacity-60">Dispatches the 4-digit delivery OTP to the buyer on checkout.</p>
          <h4 className="font-bold text-sm pt-1">MSG91 Dual Sender Numbers (WABA)</h4>
          <div className="grid md:grid-cols-2 gap-3">
            <Field s={s} set={set} k="msg91_transactional_number" label="Transactional / Utility Number" />
            <Field s={s} set={set} k="msg91_marketing_number" label="Marketing / Corporate Number" />
          </div>
          <p className="text-xs opacity-60">Utility &amp; authentication templates (attendance, fee receipts, OTPs) dispatch via the Transactional number. Marketing templates (festival, course, newsletter) dispatch via the Marketing number — if blank, marketing sends hard-fail (never cross-routed).</p>
          <h4 className="font-bold text-sm pt-1">Facebook / Meta App</h4>
          <div className="grid md:grid-cols-2 gap-3">
            <Field s={s} set={set} k="facebook_app_id" label="App ID" />
            <Field s={s} set={set} k="facebook_app_secret" label="App Secret (Data Deletion webhook)" type="password" secret />
          </div>
          <p className="text-xs opacity-60">Used to verify Meta&apos;s signed_request on the Data Deletion callback (/api/auth/facebook/data-deletion).</p>
          <Msg91HealthCard />
        </>}
      </div>
    </div>
  );
}
